import React from 'react';
import { X, Calendar, FileText, User, Clock, IndianRupee } from 'lucide-react';

const ViewPaymentModal = ({ isOpen, onClose, payment }) => {
  if (!isOpen || !payment) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentModeLabel = (mode) => {
    const modeMap = {
      'cash': 'Cash',
      'cheque': 'Cheque',
      'neft': 'NEFT',
      'rtgs': 'RTGS',
      'upi': 'UPI',
      'dd': 'Demand Draft',
      'credit': 'Credit',
      'other': 'Other'
    };
    return modeMap[mode] || mode;
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <IndianRupee className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Payment Details</h3>
              <p className="text-xs text-gray-500">Payment ID: {payment?.id}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Two-Column Body ── */}
        <div className="flex flex-col md:flex-row min-h-0">

          {/* LEFT COLUMN */}
          <div className="md:w-2/5 px-6 py-5 border-b md:border-b-0 md:border-r border-gray-200 flex flex-col gap-4">

            {/* Payment Amount Highlight */}
            <div className="border border-green-200 rounded-lg overflow-hidden">
              <div className="bg-green-50 px-4 py-2 border-b border-green-200">
                <p className="text-xs font-semibold text-green-700 uppercase tracking-wide">Payment Amount</p>
              </div>
              <div className="px-4 py-4">
                <p className="text-3xl font-bold text-green-600">₹{(payment?.amount || 0).toFixed(2)}</p>
              </div>
            </div>

            {/* Bank Payment & TDS Box */}
            {(payment?.bank_payment !== undefined || payment?.tds !== undefined) && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Payment Breakdown</p>
                </div>
                <div className="grid grid-cols-2 divide-x divide-gray-200">
                  <div className="px-4 py-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Bank Payment</p>
                    <p className="text-sm font-bold text-blue-700">₹{(payment?.bank_payment || 0).toFixed(2)}</p>
                  </div>
                  <div className="px-4 py-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">TDS</p>
                    <p className="text-sm font-bold text-orange-600">₹{(payment?.tds || 0).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Created Info Box */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Record Info</p>
              </div>
              <div className="px-4 py-3 space-y-3">
                <div>
                  <p className="text-xs font-medium text-gray-500 flex items-center gap-1 mb-1">
                    <Clock className="w-3.5 h-3.5" />Created At
                  </p>
                  <p className="text-sm text-gray-800">{formatDate(payment?.created_at)}</p>
                  <p className="text-xs text-gray-500">{formatTime(payment?.created_at)}</p>
                </div>
                {payment?.created_by_user && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 flex items-center gap-1 mb-1">
                      <User className="w-3.5 h-3.5" />Created By
                    </p>
                    <p className="text-sm text-gray-800">{payment?.created_by_user}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="md:w-3/5 px-6 py-5 flex flex-col gap-4">

            {/* Row 1: Payment Date + Payment Mode */}
            <div className="grid grid-cols-1 gap-3">
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />Payment Date
                  </p>
                </div>
                <div className="px-3 py-2.5">
                  <p className="text-sm font-medium text-gray-900">{formatDate(payment?.payment_date)}</p>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Payment Mode</p>
                </div>
                <div className="px-3 py-2.5">
                  <span className="inline-block px-2.5 py-0.5 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
                    {getPaymentModeLabel(payment?.payment_mode)}
                  </span>
                </div>
              </div>
            </div>

            {/* Row 2: Reference No + Remarks */}
            <div className="grid grid-cols-1 gap-3">
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <FileText className="w-3.5 h-3.5" />Reference No
                  </p>
                </div>
                <div className="px-3 py-2.5">
                  <p className="text-sm text-gray-900 font-medium">{payment?.reference_no || <span className="text-gray-400 font-normal">—</span>}</p>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Remarks</p>
                </div>
                <div className="px-3 py-2.5">
                  <p className="text-sm text-gray-700">{payment?.remarks || <span className="text-gray-400">—</span>}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewPaymentModal;